import Image from 'next/image'
import { ComponentType, FC, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'

import { useAddRequestFeedback } from '@/api/models/requests/hooks/useAddRequestFeedback'
import {
  RateParameters,
  RatingValue,
  RequestFeedback
} from '@/api/models/requests/lib/requests.types'
import { SOMETHING_WENT_WRONG } from '@/app/constants'
import { cn } from '@/app/utils'
import StarGold from '@/assets/img/StarGold.png'
import StarSilver from '@/assets/img/StarSilver.png'
import StarDefault from '@/assets/svg/StarDefault.svg'
import { ErrorForbidden } from '@/components/pages/plugs'
import { AppButton } from '@/components/shared/button'
import AppCheckbox from '@/components/shared/checkbox/ui/AppCheckbox'
import { ButtonSkeleton } from '@/components/shared/skeleton'
import { AppTextarea } from '@/components/shared/textarea'
import {
  MAX_COMMENT_LENGTH,
  MAX_COMMENT_LENGTH_MESSAGE_FROM
} from '@/utils/constants'
import { isForbiddenError } from '@/utils/isForbiddenError'

import { ModalName } from './types/types'

type Props = {
  id: string
  isButtonUnsolvedAvailable: boolean
  onClose: () => void
  setModalName: (_: ModalName) => void
  refetchRequest: () => void
  rateParameters?: RateParameters[]
  areRateParametersLoading: boolean
  isRateParametersError: boolean
  rateParametersError?: Error | null
}

type FeedbackFormData = {
  rating: RatingValue | null
  selectedTexts: Array<string>
  comment: string
}

const StarGoldIcon = () => (
  <Image src={StarGold.src} alt="star-gold" width={40} height={40} />
)
const StarSilverIcon = () => (
  <Image src={StarSilver.src} alt="star-silver" width={40} height={40} />
)

const AddRatingForm: FC<Props> = ({
  id,
  isButtonUnsolvedAvailable,
  onClose,
  setModalName,
  refetchRequest,
  rateParameters,
  areRateParametersLoading,
  isRateParametersError,
  rateParametersError
}) => {
  const { mutateAsync: addFeedback } = useAddRequestFeedback(id)

  const {
    control,
    formState: { errors },
    handleSubmit,
    setError,
    watch,
    setValue,
    reset
  } = useForm<FeedbackFormData>({
    defaultValues: {
      rating: null,
      selectedTexts: [],
      comment: ''
    },
    mode: 'onSubmit'
  })

  const [hoveredRating, setHoveredRating] = useState<RatingValue | null>(null)

  const selectedRating = watch('rating')
  const selectedTexts = watch('selectedTexts')

  const maxRating = (): number =>
    rateParameters ? Math.max(...rateParameters.map(item => item.rating)) : 0

  const handleCheckboxChange = (text: string, isChecked: boolean): void => {
    const currentTexts = [...selectedTexts]
    const updatedTexts = isChecked
      ? [...currentTexts, text]
      : currentTexts.filter(item => item !== text)
    setValue('selectedTexts', updatedTexts)
  }

  const handleSelectedRating = (rating: RatingValue): void => {
    setValue('rating', rating)
    setValue('selectedTexts', [])
  }

  const getStarComponent = (rating: number): ComponentType => {
    const isSelected = selectedRating !== null && rating <= selectedRating
    const isHovered =
      hoveredRating !== null &&
      selectedRating === null &&
      rating <= hoveredRating

    if (isSelected) {
      return selectedRating === maxRating() ? StarGoldIcon : StarSilverIcon
    }

    if (isHovered) {
      return hoveredRating === maxRating() ? StarGoldIcon : StarSilverIcon
    }

    return StarDefault
  }

  const handleStarHover = (rating: RatingValue | null): void => {
    if (selectedRating === null) {
      setHoveredRating(rating)
    }
  }

  const textComment =
    selectedRating === maxRating()
      ? 'Расскажите нам, пожалуйста, что вам понравилось'
      : 'Расскажите нам, пожалуйста, что произошло'

  const onSubmit = async (formData: FeedbackFormData): Promise<void> => {
    if (formData.comment.length > MAX_COMMENT_LENGTH) {
      setError('comment', {
        message: `Не более ${MAX_COMMENT_LENGTH} символов`
      })
      return
    }

    if (formData.rating) {
      const body: RequestFeedback = {
        rating: formData.rating,
        rateParameters: formData.selectedTexts,
        comment: formData.comment
      }

      addFeedback({ body })
        .then(() => {
          refetchRequest()
          setModalName(ModalName.FeedbackCompleted)
        })
        .catch(() => {
          reset()
          onClose()
        })
    }
  }

  const handleOpenModal = (): void => {
    onClose()
    setModalName(ModalName.ProblemNotSolved)
  }

  if (areRateParametersLoading) {
    return (
      <div className="mt-32 flex w-[300px] justify-center gap-12 sm:flex sm:h-full sm:flex-grow sm:items-center">
        {Array.from({ length: 5 }).map((_, index) => (
          <ButtonSkeleton key={index} className="h-40 w-40 !rounded-full" />
        ))}
      </div>
    )
  }

  if (isForbiddenError(rateParametersError ?? null)) {
    return (
      <div className="flex h-full w-[400px] flex-col sm:w-full">
        <div className="flex flex-grow flex-col items-center justify-center">
          <ErrorForbidden className="my-0 !max-w-full" />
        </div>
      </div>
    )
  }

  if (isRateParametersError) {
    return (
      <div className="flex h-full w-[400px] flex-col gap-12 sm:w-full">
        <div className="flex flex-grow flex-col items-center justify-center gap-20">
          <div className="flex w-full justify-center">
            <Image
              src="/images/shared/something-went-wrong.png"
              alt="image"
              className="rotate-12"
              width={120}
              height={120}
            />
          </div>
          <p className="w-full text-center text-grey-70 text-20 sm:text-18">
            {SOMETHING_WENT_WRONG}
          </p>
        </div>
        <AppButton
          variant="secondary"
          className="!h-56"
          fluid
          onClick={onClose}
        >
          Понятно
        </AppButton>
      </div>
    )
  }

  return (
    <form
      className="sm:flex sm:h-full sm:flex-grow sm:flex-col"
      onSubmit={handleSubmit(onSubmit)}
    >
      <div className="mt-32 sm:flex sm:h-full sm:flex-grow sm:flex-col sm:justify-center">
        <div className="flex justify-center gap-12">
          {rateParameters?.map(({ rating }, index) => {
            const StarComponent = getStarComponent(rating)

            return (
              <button
                aria-label={`Оценка: ${index + 1} из 5 звёзд`}
                key={rating}
                onClick={() => handleSelectedRating(rating)}
                onMouseEnter={() => handleStarHover(rating)}
                onMouseLeave={() => handleStarHover(null)}
                type="button"
                className="focus:outline-none"
              >
                <StarComponent />
              </button>
            )
          })}
        </div>

        {selectedRating !== null && (
          <>
            <div className="mb-8 mt-20 flex w-full flex-col justify-center gap-8">
              <p className="text-center font-bold text-28">Спасибо!</p>
              <p className="max-w-[400px] text-center text-grey-70 text-20 sm:mx-auto sm:text-18">
                Поделитесь своими впечатлениями, ваш отзыв поможет нам стать еще
                лучше
              </p>
              <hr className="my-12 text-grey-30" />
              <div className="flex flex-col gap-20">
                {rateParameters?.[selectedRating - 1]?.rateParameters.map(
                  text => (
                    <div key={text} className="flex items-start gap-8">
                      <AppCheckbox
                        id={text}
                        className="relative top-3"
                        checked={selectedTexts.includes(text)}
                        onChange={e =>
                          handleCheckboxChange(text, e.target.checked)
                        }
                      />
                      <label
                        htmlFor={text}
                        className="cursor-pointer select-none font-pro text-16"
                      >
                        {text}
                      </label>
                    </div>
                  )
                )}
              </div>
            </div>
            <Controller
              name="comment"
              control={control}
              render={({ field }) => (
                <AppTextarea
                  {...field}
                  value={field.value ?? ''}
                  placeholder={textComment}
                  onChange={field.onChange}
                  maxLength={MAX_COMMENT_LENGTH}
                  maxLengthMessageFrom={MAX_COMMENT_LENGTH_MESSAGE_FROM}
                  invalid={Boolean(errors?.comment?.message)}
                  invalidMessage={errors?.comment?.message}
                />
              )}
            />
          </>
        )}

        {selectedRating === null && (
          <div className="mt-20 flex w-full justify-center">
            <p className="max-w-[400px] text-center text-grey-70 text-20 sm:text-18">
              Поделитесь своими впечатлениями и помогите улучшить качество
              обслуживания. <br /> Ваш отзыв поможет нам стать еще лучше.
            </p>
          </div>
        )}
      </div>

      <div
        className={cn(
          'mt-20 flex flex-col gap-12',
          selectedRating && 'sm:pb-20'
        )}
      >
        {isButtonUnsolvedAvailable && !selectedRating && (
          <AppButton
            type="button"
            variant="backless"
            className="!h-56"
            fluid
            onClick={handleOpenModal}
          >
            Проблему не решили
          </AppButton>
        )}

        {selectedRating && (
          <AppButton type="submit" variant="secondary" className="!h-56" fluid>
            Отправить
          </AppButton>
        )}
      </div>
    </form>
  )
}

export { AddRatingForm }
